import React, { useEffect, useState, useCallback, useContext, useRef, useMemo } from 'react';
import { Container, Row, Col, Button, Form, Modal, Spinner, InputGroup, Pagination, Table, Badge, Accordion } from 'react-bootstrap';
import { 
  FaMapMarkerAlt,
  FaSearch, FaTimes, FaPlus, FaMinus, FaTrash, FaShoppingCart, 
  FaUser, FaPhoneAlt, FaEnvelope, FaCheckCircle, 
  FaExclamationTriangle, FaThLarge, FaList, FaCreditCard, 
  FaMoneyBillWave, FaQrcode, FaSyncAlt, FaPills, 
  FaCheck, FaCoins, FaFire, FaUniversity, FaReceipt,
  FaAngleDoubleLeft, FaAngleDoubleRight, FaChevronLeft, FaChevronRight,
  FaFilePrescription, FaTag, FaArrowDown, FaEye, FaSearchPlus, FaSearchMinus, FaRedo
} from 'react-icons/fa';
import { get, post, BASE_URL } from '../utils/api';
import { CartContext } from '../utils/CartContext';
import { useCurrency } from '../utils/CurrencyContext';
import { useToast } from './ToastContext';
import NoDataCard from './NoDataCard';
import ProgressDialog from './ProgressDialog';
import './OfflineSell.css';

export default function OfflineSell() {
  const [batches, setBatches] = useState([]);
  const [medicineList, setMedicineList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSellModal, setShowSellModal] = useState(false);
  
  // Search & Pagination States
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);
  const [totalMedicines, setTotalMedicines] = useState(0);

  // Filter & View States
  const [quantities, setQuantities] = useState({});
  const [selectedUnits, setSelectedUnits] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [sortBy, setSortBy] = useState('most_ordered');
  const [viewMode, setViewMode] = useState('grid');

  const { cart, addToCart, removeFromCart, increaseQuantity, decreaseQuantity, updateQuantity, clearCart, updateItemUnit } = useContext(CartContext);
  const { currencySymbol } = useCurrency();
  const { showToast } = useToast();

  // Dynamic Payment Methods from Backend
  const [paymentMethods, setPaymentMethods] = useState([
    { id: 'cash', name: 'Cash', icon: 'cash' },
    { id: 'card', name: 'Card', icon: 'card' },
    { id: 'online', name: 'UPI / Online QR', icon: 'upi' },
    { id: 'bank_transfer', name: 'Bank Transfer', icon: 'bank' },
    { id: 'others', name: 'Other', icon: 'others' },
  ]);

  // Customer fields for offline sale
  const [customerMobile, setCustomerMobile] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerLookupLoading, setCustomerLookupLoading] = useState(false);
  const [customerFound, setCustomerFound] = useState(false);
  
  // Doctor & Prescription fields
  const [prescriptionFile, setPrescriptionFile] = useState(null);
  const [prescriptionPreview, setPrescriptionPreview] = useState(null);
  const [prescriptionUploading, setPrescriptionUploading] = useState(false);
  const [fetchedPrescription, setFetchedPrescription] = useState(null);
  const [fetchingPrescription, setFetchingPrescription] = useState(false);
  const [prescriptionLookupError, setPrescriptionLookupError] = useState(null);
  const [showPrescriptionViewerModal, setShowPrescriptionViewerModal] = useState(false);
  const [activePrescriptionViewUrl, setActivePrescriptionViewUrl] = useState('');
  const [rxZoom, setRxZoom] = useState(1);
  const [rxRotate, setRxRotate] = useState(0);

  const [doctorName, setDoctorName] = useState('');
  const [doctorPhone, setDoctorPhone] = useState('');
  const [doctorRegistrationNumber, setDoctorRegistrationNumber] = useState('');
  const [prescriptionId, setPrescriptionId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [remarks, setRemarks] = useState('');
  const [lastOrderId, setLastOrderId] = useState(null);
  const [showSendEmailPrompt, setShowSendEmailPrompt] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [previousOrders, setPreviousOrders] = useState([]);
  const [loadingPreviousOrders, setLoadingPreviousOrders] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const mobileDebounce = useRef(null);
  const debounceTimeout = useRef(null);
  const rxLookupDebounce = useRef(null);
  const catalogTopRef = useRef(null);
  const registerRef = useRef(null);

  const fetchPrescriptionById = useCallback(async (id) => {
    if (!id || id.toString().trim() === '') {
      setFetchedPrescription(null);
      setPrescriptionLookupError(null);
      return;
    }

    setFetchingPrescription(true);
    setPrescriptionLookupError(null);
    try {
      const res = await get(`/prescriptions/${id.toString().trim()}`);
      if (res && res.success && res.data) {
        setFetchedPrescription(res.data);
        setPrescriptionLookupError(null);
        if (res.data.doctor_name) setDoctorName(res.data.doctor_name);
        if (res.data.doctor_phone) setDoctorPhone(res.data.doctor_phone);
        if (res.data.doctor_registration_number) setDoctorRegistrationNumber(res.data.doctor_registration_number);
        if (prescriptionPreview) {
          URL.revokeObjectURL(prescriptionPreview);
        }
        setPrescriptionFile(null);
        setPrescriptionPreview(null);
      } else {
        setFetchedPrescription(null);
        setPrescriptionLookupError('Prescription not found with this ID');
      }
    } catch (err) {
      setFetchedPrescription(null);
      setPrescriptionLookupError('Prescription not found');
    } finally {
      setFetchingPrescription(false);
    }
  }, [prescriptionPreview]);

  const handlePrescriptionIdInput = (val) => {
    setPrescriptionId(val);
    if (rxLookupDebounce.current) {
      clearTimeout(rxLookupDebounce.current);
    }
    if (!val || val.trim() === '') {
      setFetchedPrescription(null);
      setPrescriptionLookupError(null);
      return;
    }
    rxLookupDebounce.current = setTimeout(() => {
      fetchPrescriptionById(val);
    }, 500);
  };

  const handleClearFetchedPrescription = () => {
    setPrescriptionId('');
    setFetchedPrescription(null);
    setPrescriptionLookupError(null);
  };

  const handlePrescriptionFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showToast('Please select a valid image file (JPG, PNG, WebP).', 'warning');
        return;
      }
      setPrescriptionFile(file);
      setPrescriptionPreview(URL.createObjectURL(file));
      setPrescriptionId(''); // Clear ID if uploading new file
      setFetchedPrescription(null);
      setPrescriptionLookupError(null);
    }
  };

  const handleRemovePrescription = () => {
    if (prescriptionPreview) {
      URL.revokeObjectURL(prescriptionPreview);
    }
    setPrescriptionFile(null);
    setPrescriptionPreview(null);
  };

  const resetForm = () => {
    setCustomerMobile('');
    setCustomerName('');
    setCustomerEmail('');
    setCustomerFound(false);
    setPrescriptionFile(null);
    setPrescriptionPreview(null);
    setPrescriptionUploading(false);
    setFetchedPrescription(null);
    setFetchingPrescription(false);
    setPrescriptionLookupError(null);
    setDoctorName('');
    setDoctorPhone('');
    setDoctorRegistrationNumber('');
    setPrescriptionId('');
    setPaymentMethod('cash');
    setRemarks('');
    setPreviousOrders([]);
    setShowHistoryModal(false);
  };

  // Fetch supported payment methods & master categories from backend
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [methodsRes, catRes] = await Promise.allSettled([
          get('/payments/methods'),
          get('/medicines/categories')
        ]);

        if (methodsRes.status === 'fulfilled' && methodsRes.value?.success && Array.isArray(methodsRes.value.data) && methodsRes.value.data.length > 0) {
          setPaymentMethods(methodsRes.value.data);
        }
        if (catRes.status === 'fulfilled' && catRes.value?.success && Array.isArray(catRes.value.data)) {
          setCategoriesList(catRes.value.data);
        }
      } catch (err) {
        // keep defaults
      }
    };
    fetchInitialData();
  }, []);

  const getPaymentIcon = (iconName) => {
    switch (iconName) {
      case 'cash': return <FaMoneyBillWave className="me-1" />;
      case 'card': return <FaCreditCard className="me-1" />;
      case 'upi':
      case 'online': return <FaQrcode className="me-1" />;
      case 'bank':
      case 'bank_transfer': return <FaUniversity className="me-1" />;
      default: return <FaCoins className="me-1" />;
    }
  };

  // Determine if any item in the cart requires a prescription
  const requiresPrescription = useMemo(() => {
    if (!cart || !cart.items || cart.items.length === 0) return false;
    return cart.items.some(item => {
      if (item.prescription_required || item.requires_prescription || item.prescriptionRequired) return true;
      const batch = batches.find(b => b.batch_id === item.batch_id || b.batch_id === item.id || b.medicine_id === item.medicine_id);
      if (batch && (batch.prescription_required || batch.requires_prescription || batch.prescriptionRequired)) return true;
      const med = medicineList.find(m => m.medicine_id === item.medicine_id || m.medicine_id === item.batch_id);
      if (med && (med.prescription_required || med.requires_prescription || med.prescriptionRequired)) return true;
      return false;
    });
  }, [cart, batches, medicineList]);

  const handleQuantityChange = (batch, value) => {
    const unit = selectedUnits[batch.batch_id] || 'piece';
    let factor = 1;
    if (unit === 'strip') factor = batch.strip_conversion || 1;
    else if (unit === 'box') factor = batch.box_conversion || 1;

    const maxQty = Math.floor(batch.stock_quantity / factor);
    if (maxQty <= 0) {
      setQuantities(prev => ({ ...prev, [batch.batch_id]: 0 }));
      return;
    }
    if (value === '') {
      setQuantities(prev => ({ ...prev, [batch.batch_id]: '' }));
      return;
    }
    const num = parseInt(value, 10);
    if (isNaN(num) || num <= 0) {
      setQuantities(prev => ({ ...prev, [batch.batch_id]: 1 }));
      return;
    }
    const safeQty = Math.min(Math.max(1, num), maxQty);
    setQuantities(prev => ({ ...prev, [batch.batch_id]: safeQty }));
  };

  const handleIncrement = (batch) => {
    const unit = selectedUnits[batch.batch_id] || 'piece';
    let factor = 1;
    if (unit === 'strip') factor = batch.strip_conversion || 1;
    else if (unit === 'box') factor = batch.box_conversion || 1;
    
    const maxQty = Math.floor(batch.stock_quantity / factor);
    if (maxQty <= 0) {
      showToast(`Cannot add ${unit}. Insufficient stock (Requires ${factor} base units, but only ${batch.stock_quantity} available).`, 'warning');
      return;
    }
    const current = Number(quantities[batch.batch_id]) || 1;
    if (current >= maxQty) {
      showToast(`Stock limit reached: Maximum ${maxQty} ${unit}(s) available.`, 'warning');
      return;
    }
    setQuantities(prev => ({
      ...prev,
      [batch.batch_id]: Math.min(current + 1, maxQty)
    }));
  };

  const handleDecrement = (batch) => {
    const current = Number(quantities[batch.batch_id]) || 1;
    setQuantities(prev => ({
      ...prev,
      [batch.batch_id]: Math.max(1, current - 1)
    }));
  };

  // Unified Fetch Data with multi-field search and category filtering
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const medicineParams = new URLSearchParams();
      if (searchTerm && searchTerm.trim() !== '') {
        medicineParams.append('q', searchTerm.trim());
      }
      if (selectedCategory && selectedCategory !== 'all') {
        medicineParams.append('category', selectedCategory);
      }
      medicineParams.append('page', currentPage);
      medicineParams.append('limit', itemsPerPage);
      medicineParams.append('status', 'active');
      medicineParams.append('sort_by', sortBy);

      const medicinesEndpoint = `/medicines?${medicineParams.toString()}`;
      const medicinesResponse = await get(medicinesEndpoint);
      const medicines = medicinesResponse.data || [];
      setMedicineList(medicines);

      const flattened = medicines.flatMap((m) => (m.batches || []).map((b) => ({
        batch_id: b.id,
        batch_number: b.name,
        stock_quantity: b.stock,
        selling_price: b.price,
        expiry_date: b.expiry,
        medicine_id: m.medicine_id,
        medicine_name: m.name,
        manufacturer: m.manufacturer,
        categories: m.categories || [],
        image_url: m.image_url || m.image || null,
        prescription_required: m.prescription_required || m.prescriptionRequired || 0,
        unit_name: m.unit_name || 'piece',
        strip_conversion: m.strip_conversion || 1,
        box_conversion: m.box_conversion || 1,
        total_sold: m.total_sold || 0,
        total_orders: m.total_orders || 0,
          rack_locations: m.rack_locations || [],
      })));

      setBatches(flattened);
      const total = medicinesResponse.pagination?.total_items ?? medicinesResponse.total ?? medicinesResponse.totalItems ?? flattened.length;
      setTotalMedicines(total);
    } catch (err) {
      console.error("[FETCH DATA ERROR]:", err);
      setError(err.response?.data?.message || 'Failed to fetch medicines catalog.');
      setMedicineList([]);
      setBatches([]);
      setTotalMedicines(0);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCategory, currentPage, itemsPerPage, sortBy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
      if (mobileDebounce.current) clearTimeout(mobileDebounce.current);
    };
  }, []);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    if (catalogTopRef.current) {
      catalogTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleAddToCart = (batch, quantity = 1) => {
    const parsedQty = Number(quantity) > 0 ? Number(quantity) : 1;
    const unit = selectedUnits[batch.batch_id] || 'piece';
    let factor = 1;
    if (unit === 'strip') factor = batch.strip_conversion || 1;
    else if (unit === 'box') factor = batch.box_conversion || 1;

    const maxAvailable = Math.floor(batch.stock_quantity / factor);
    if (maxAvailable <= 0) {
      showToast(`Cannot add ${unit}! 1 ${unit} requires ${factor} base units, but only ${batch.stock_quantity} are available in stock.`, 'error');
      return;
    }

    // Check what is already in cart for this batch in base units
    const currentCartQtyInBaseUnits = (cart?.items || [])
      .filter(item => item.batch_id === batch.batch_id)
      .reduce((sum, item) => sum + (item.quantity * (item.conversion_factor || 1)), 0);

    const requestedBaseUnits = parsedQty * factor;
    const remainingBaseUnits = batch.stock_quantity - currentCartQtyInBaseUnits;

    if (requestedBaseUnits > remainingBaseUnits) {
      const remainingInUnit = Math.floor(remainingBaseUnits / factor);
      if (remainingInUnit <= 0) {
        showToast(`Stock limit reached for "${batch.medicine_name}"! You already have ${currentCartQtyInBaseUnits} base units in your cart, and only ${remainingBaseUnits} remain in stock.`, 'error');
      } else {
        showToast(`Cannot add ${parsedQty} ${unit}(s). Only ${remainingInUnit} ${unit}(s) (${remainingBaseUnits} base units) remain available in stock.`, 'error');
      }
      return;
    }

    const unitPrice = parseFloat(batch.selling_price) * factor;

    addToCart({
      id: `${batch.batch_id}_${unit}`,
      name: `${batch.medicine_name || 'Medicine'}`,
      display_unit: unit.toUpperCase(),
      price: unitPrice,
      quantity: parsedQty,
      batch_id: batch.batch_id,
      batch_number: batch.batch_number,
      selected_unit: unit,
      conversion_factor: factor,
      prescription_required: batch.prescription_required,
      selling_price: batch.selling_price,
      strip_conversion: batch.strip_conversion || 1,
      box_conversion: batch.box_conversion || 1,
      stock_quantity: batch.stock_quantity,
      unit_name: batch.unit_name || 'piece',
      gst_rate: batch.gst_rate !== undefined && batch.gst_rate !== null ? parseFloat(batch.gst_rate) : 12,
      hsn_code: batch.hsn_code || '3004',
        rack_location: (batch.rack_locations && batch.rack_locations[0]) ? `${batch.rack_locations[0].rack_name} - ${batch.rack_locations[0].slot_code}` : null,
    });

    showToast(`Added ${parsedQty} ${unit}(s) of ${batch.medicine_name} to cart`, 'success');
  };

  const handleAddFromHistory = async (item) => {
    // 1. Check if the batch is in the currently loaded batches
    let targetBatch = batches.find(b => b.batch_id === item.batch_id);
    
    // 2. If not found locally, fetch the medicine catalog to find it
    if (!targetBatch) {
      try {
        showToast(`Loading details for ${item.medicine_name}...`, 'info');
        const res = await get(`/medicines?q=${encodeURIComponent(item.medicine_name)}`);
        if (res && res.success && Array.isArray(res.data)) {
          // Find the medicine
          const med = res.data.find(m => m.name === item.medicine_name || m.medicine_id === item.medicine_id);
          if (med && Array.isArray(med.batches)) {
            // Find the batch
            const b = med.batches.find(batch => batch.id === item.batch_id || batch.name === item.batch_number);
            if (b) {
              targetBatch = {
                batch_id: b.id,
                batch_number: b.name,
                stock_quantity: b.stock,
                selling_price: b.price,
                expiry_date: b.expiry,
                medicine_id: med.medicine_id,
                medicine_name: med.name,
                manufacturer: med.manufacturer,
                categories: med.categories || [],
                image_url: med.image_url || med.image || null,
                prescription_required: med.prescription_required || med.prescriptionRequired || 0,
                unit_name: med.unit_name || 'piece',
                strip_conversion: med.strip_conversion || 1,
                box_conversion: med.box_conversion || 1,
                total_sold: med.total_sold || 0,
                total_orders: med.total_orders || 0,
              };
            }
          }
        }
      } catch (err) {
        showToast(`Failed to load details for ${item.medicine_name}`, 'error');
        return;
      }
    }

    if (!targetBatch) {
      showToast(`Medicine batch is no longer available in the store catalog.`, 'error');
      return;
    }

    // Check if the item has stock
    if (targetBatch.stock_quantity <= 0) {
      showToast(`"${targetBatch.medicine_name}" (Batch #${targetBatch.batch_number}) is out of stock.`, 'error');
      return;
    }

    // Set the selected unit for this batch to match what was previously ordered
    // or fallback to unit_name/piece
    const targetUnit = item.selected_unit || targetBatch.unit_name || 'piece';
    setSelectedUnits(prev => ({
      ...prev,
      [targetBatch.batch_id]: targetUnit
    }));

    // Add to cart (defaulting to 1 unit of that selection)
    handleAddToCart(targetBatch, 1);
  };

  const handleIncreaseCartQuantity = (item) => {
    const batch = batches.find(b => b.batch_id === item.batch_id);
    if (!batch) {
      increaseQuantity(item.id);
      return;
    }
    const currentCartQtyInBaseUnits = (cart?.items || [])
      .filter(i => i.batch_id === item.batch_id)
      .reduce((sum, i) => sum + (i.quantity * (i.conversion_factor || 1)), 0);

    const additionalNeeded = item.conversion_factor || 1;
    if (currentCartQtyInBaseUnits + additionalNeeded > batch.stock_quantity) {
      showToast(`Cannot add more ${item.display_unit}. Max available stock is ${batch.stock_quantity} base units.`, 'warning');
      return;
    }
    increaseQuantity(item.id);
  };

  const handleUpdateCartQuantity = (item, newQty) => {
    const batch = batches.find(b => b.batch_id === item.batch_id);
    const val = parseInt(newQty, 10);
    if (isNaN(val) || val <= 0) return;

    if (!batch) {
      updateQuantity(item.id, val);
      return;
    }

    const otherItemsBaseUnits = (cart?.items || [])
      .filter(i => i.batch_id === item.batch_id && i.id !== item.id)
      .reduce((sum, i) => sum + (i.quantity * (i.conversion_factor || 1)), 0);

    const requestedBaseUnits = val * (item.conversion_factor || 1);
    if (otherItemsBaseUnits + requestedBaseUnits > batch.stock_quantity) {
      const maxAllowed = Math.floor((batch.stock_quantity - otherItemsBaseUnits) / (item.conversion_factor || 1));
      showToast(`Exceeds available stock. Max allowed for ${item.display_unit}: ${maxAllowed}`, 'warning');
      if (maxAllowed > 0) {
        updateQuantity(item.id, maxAllowed);
      }
      return;
    }
    updateQuantity(item.id, val);
  };

  const handleCartItemUnitChange = (item, newUnit) => {
    let factor = 1;
    if (newUnit === 'strip') factor = item.strip_conversion || 1;
    else if (newUnit === 'box') factor = item.box_conversion || 1;

    // Check if the total stock is enough for the current quantity in this new unit
    const currentBaseQty = item.quantity * factor;
    // We need to calculate what is in the cart from other selections of the same batch
    const otherSelectionsBaseQty = (cart?.items || [])
      .filter(i => i.batch_id === item.batch_id && i.id !== item.id)
      .reduce((sum, i) => sum + (i.quantity * (i.conversion_factor || 1)), 0);

    const totalNeededBaseUnits = currentBaseQty + otherSelectionsBaseQty;
    const maxStock = item.stock_quantity ?? 99999;
    if (totalNeededBaseUnits > maxStock) {
      showToast(`Cannot change to ${newUnit}! Requires ${totalNeededBaseUnits} base units in total, but only ${maxStock} are in stock.`, 'error');
      return;
    }

    const basePrice = parseFloat(item.selling_price || (item.price / (item.conversion_factor || 1)));
    const newPrice = basePrice * factor;

    updateItemUnit(item.id, newUnit, newPrice, factor);
    showToast(`Updated "${item.name}" unit to ${newUnit.toUpperCase()}`, 'success');
  };

  const handleSellConfirm = async () => {
    if (cart.items.length === 0) {
      showToast('Cart is empty. Please add items before checkout.', 'error');
      return;
    }

    if (!customerMobile || customerMobile.trim() === '') {
      showToast('Please enter customer mobile number before confirming.', 'error');
      return;
    }

    if (requiresPrescription) {
      if (!prescriptionFile && !prescriptionId && (!doctorName || !doctorPhone || !doctorRegistrationNumber)) {
        showToast('Prescription required: Please upload a prescription image or provide doctor details.', 'error');
        return;
      }
    }

    try {
      setLoading(true);

      let finalPrescriptionId = prescriptionId && prescriptionId.trim() !== '' ? prescriptionId : null;

      // If a prescription file was attached, upload it first
      if (prescriptionFile) {
        setPrescriptionUploading(true);
        const formData = new FormData();
        formData.append('prescription_image', prescriptionFile);
        if (doctorName) formData.append('doctor_name', doctorName);
        if (doctorPhone) formData.append('doctor_phone', doctorPhone);
        if (doctorRegistrationNumber) formData.append('doctor_registration_number', doctorRegistrationNumber);

        const uploadRes = await post('/prescriptions', formData);
        setPrescriptionUploading(false);
        if (uploadRes && uploadRes.success && uploadRes.prescription_id) {
          finalPrescriptionId = uploadRes.prescription_id;
        } else {
          showToast(uploadRes?.message || 'Failed to upload prescription image.', 'error');
          setLoading(false);
          return;
        }
      }

      const saleData = {
        order_type: 'offline',
        items: cart.items.map(item => ({
          batch_id: item.batch_id,
          quantity: item.quantity,
          selected_unit: item.selected_unit || 'piece',
          conversion_factor: item.conversion_factor || 1,
          gst_rate: item.gst_rate !== undefined ? item.gst_rate : 12,
          tax_rate: item.gst_rate !== undefined ? item.gst_rate : 12,
        })),
        payment_method: paymentMethod,
        customer_name: customerName || null,
        customer_phone: customerMobile || null,
        customer_email: customerEmail || null,
        remarks: remarks || null,
      };

      if (requiresPrescription) {
        if (finalPrescriptionId) {
          saleData.prescription_id = finalPrescriptionId;
        } else {
          saleData.doctor_name = doctorName;
          saleData.doctor_phone = doctorPhone;
          saleData.doctor_registration_number = doctorRegistrationNumber;
        }
      }

      const response = await post('/orders', saleData);
      if (response && response.success) {
        const orderId = response.order_id || response.data?.order_id;
        const status = response.status || response.data?.status;
        showToast(`Sale processed successfully! Order #${orderId} (${status || 'CONFIRMED'})`, 'success');
        setShowSellModal(false);
        clearCart();
        fetchData();
        resetForm();
        setLastOrderId(orderId || null);
        if (customerEmail && customerEmail.trim() !== '') {
          setShowSendEmailPrompt(true);
        }
      } else {
        showToast(response?.message || 'Failed to process sale.', 'error');
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || err.message || 'Failed to process sale. Please check item stock quantities.';
      showToast(errMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendBillEmail = async () => {
    if (!lastOrderId) return;
    setSendingEmail(true);
    try {
      const res = await post(`/orders/${lastOrderId}/bill/email`);
      if (res && res.success) {
        showToast(res.message || 'Bill sent to customer email successfully', 'success');
      } else {
        showToast(res?.message || 'Failed to send bill to email', 'error');
      }
    } catch (e) {
      showToast(e.message || 'Failed to send bill to email', 'error');
    } finally {
      setSendingEmail(false);
      setShowSendEmailPrompt(false);
      setLastOrderId(null);
    }
  };

  // Combine unique categories from API and current batches
  const allCategories = useMemo(() => {
    const set = new Set();
    categoriesList.forEach(c => set.add(typeof c === 'string' ? c : c.name));
    batches.forEach(b => {
      (b.categories || []).forEach(c => set.add(c.name || c));
    });
    return Array.from(set).filter(Boolean);
  }, [categoriesList, batches]);

  // Client-side stock filtering on current fetched page
  const displayedBatches = useMemo(() => {
    return batches.filter(batch => {
      if (stockFilter === 'in_stock' && batch.stock_quantity <= 0) return false;
      if (stockFilter === 'low_stock' && (batch.stock_quantity >= 50 || batch.stock_quantity <= 0)) return false;
      return true;
    });
  }, [batches, stockFilter]);

  const totalPages = Math.ceil(totalMedicines / itemsPerPage) || 1;
  const startItem = totalMedicines === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalMedicines);

  const cartSubtotal = useMemo(() => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((sum, item) => sum + (parseFloat(item.price || 0) * item.quantity), 0);
  }, [cart]);

  const cartTotalTax = useMemo(() => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((sum, item) => {
      const lineBase = parseFloat(item.price || 0) * item.quantity;
      const rate = parseFloat(item.gst_rate !== undefined ? item.gst_rate : 12);
      return sum + (lineBase * rate) / 100;
    }, 0);
  }, [cart]);

  const effectiveGstRate = useMemo(() => {
    if (!cart || !cart.items || cart.items.length === 0 || cartSubtotal <= 0) return 18;
    const rates = cart.items.map(it => parseFloat(it.gst_rate !== undefined ? it.gst_rate : 18));
    const allSame = rates.every(r => Math.abs(r - rates[0]) < 0.01);
    if (allSame) return rates[0];
    return (cartTotalTax / cartSubtotal) * 100;
  }, [cart, cartSubtotal, cartTotalTax]);

  const halfGstRate = useMemo(() => {
    const half = effectiveGstRate / 2;
    return Number.isInteger(half) ? half : parseFloat(half.toFixed(1));
  }, [effectiveGstRate]);

  const cartTotal = useMemo(() => {
    return cartSubtotal + cartTotalTax;
  }, [cartSubtotal, cartTotalTax]);

  const cartTotalItems = useMemo(() => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((sum, item) => sum + item.quantity, 0);
  }, [cart]);

  const getExpiryBadge = (expiryDateString) => {
    if (!expiryDateString) return <span className="expiry-pill expiry-none">No Expiry</span>;
    const expiry = new Date(expiryDateString);
    if (isNaN(expiry)) return <span className="expiry-pill expiry-none">No Expiry</span>;
    const now = new Date();
    const diffMonths = (expiry.getFullYear() - now.getFullYear()) * 12 + (expiry.getMonth() - now.getMonth());
    const formatted = expiry.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

    if (diffMonths < 0) {
      return <span className="expiry-pill expiry-expired" title="Expired"><FaExclamationTriangle className="me-1" /> Exp: {formatted}</span>;
    } else if (diffMonths <= 3) {
      return <span className="expiry-pill expiry-warning" title="Expires soon"><FaExclamationTriangle className="me-1" /> Exp: {formatted}</span>;
    } else {
      return <span className="expiry-pill expiry-good" title="Valid">Exp: {formatted}</span>;
    }
  };

  // Smart Sliding Window Pagination Generator
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    return (
      <div className="pos-pagination-container d-flex justify-content-between align-items-center flex-wrap gap-3 p-3 rounded glassmorphic-panel mt-3">
        <div className="pos-pagination-info text-muted small">
          Showing <span className="fw-bold text-white">{startItem}</span> - <span className="fw-bold text-white">{endItem}</span> of <span className="fw-bold text-white">{totalMedicines}</span> medicines
        </div>

        <div className="d-flex align-items-center gap-2">
          <Pagination className="pos-pagination-bar mb-0">
            <Pagination.First 
              onClick={() => handlePageChange(1)} 
              disabled={currentPage <= 1}
              title="First Page"
            >
              <FaAngleDoubleLeft />
            </Pagination.First>
            <Pagination.Prev 
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))} 
              disabled={currentPage <= 1}
              title="Previous Page"
            >
              <FaChevronLeft />
            </Pagination.Prev>

            {startPage > 1 && (
              <>
                <Pagination.Item onClick={() => handlePageChange(1)}>1</Pagination.Item>
                {startPage > 2 && <Pagination.Ellipsis disabled />}
              </>
            )}

            {Array.from({ length: endPage - startPage + 1 }).map((_, idx) => {
              const pageNum = startPage + idx;
              return (
                <Pagination.Item
                  key={pageNum}
                  active={pageNum === currentPage}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </Pagination.Item>
              );
            })}

            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && <Pagination.Ellipsis disabled />}
                <Pagination.Item onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>
              </>
            )}

            <Pagination.Next 
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} 
              disabled={currentPage >= totalPages}
              title="Next Page"
            >
              <FaChevronRight />
            </Pagination.Next>
            <Pagination.Last 
              onClick={() => handlePageChange(totalPages)} 
              disabled={currentPage >= totalPages}
              title="Last Page"
            >
              <FaAngleDoubleRight />
            </Pagination.Last>
          </Pagination>
        </div>

        <div className="pos-page-size-selector d-flex align-items-center gap-2">
          <span className="small text-muted">Per page:</span>
          <Form.Select
            size="sm"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="pos-page-size-dropdown"
          >
            <option value={12}>12</option>
            <option value={24}>24</option>
            <option value={36}>36</option>
            <option value={48}>48</option>
          </Form.Select>
        </div>
      </div>
    );
  };

  return (
    <Container fluid className="pos-terminal-container px-3 px-lg-4 py-3">
      <ProgressDialog show={loading && batches.length === 0} message="Loading medicines catalog..." />

      {/* POS Top Header */}
      <div className="pos-header-bar mb-3 p-3 rounded glassmorphic-panel d-flex justify-content-between align-items-center flex-wrap gap-3" ref={catalogTopRef}>
        <div>
          <div className="d-flex align-items-center gap-2">
            <div className="pos-logo-icon">
              <FaPills />
            </div>
            <div>
              <h3 className="pos-title mb-0">Point of Sale (POS)</h3>
              <p className="pos-subtitle mb-0">Fast counter dispensing, live stock conversion & instant bill settlement.</p>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="pos-quick-stat">
            <span className="stat-num">{totalMedicines}</span>
            <span className="stat-txt">Catalog Items</span>
          </div>
          <div className="pos-quick-stat">
            <span className="stat-num text-primary">{cart.items.length}</span>
            <span className="stat-txt">Cart Items</span>
          </div>
          <div className="pos-quick-stat">
            <span className="stat-num text-success">{currencySymbol}{cartTotal.toFixed(2)}</span>
            <span className="stat-txt">Cart Value</span>
          </div>
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={() => fetchData()} 
            className="pos-header-btn d-flex align-items-center gap-1"
            title="Reload catalog"
          >
            <FaSyncAlt /> Refresh
          </Button>
          {cart.items.length > 0 && (
            <Button 
              variant="outline-danger" 
              size="sm" 
              onClick={clearCart} 
              className="pos-header-btn d-flex align-items-center gap-1"
              title="Clear all cart items"
            >
              <FaTrash /> Clear Cart
            </Button>
          )}
        </div>
      </div>

      {/* Full-Width Section 1: Product Catalog & Search Filter Strip */}
      <div className="pos-catalog-section mb-4">
        {/* Controls Bar: Universal Multi-Field Search, Sort, Stock & Category Chips */}
        <div className="pos-filter-panel mb-3 p-3 rounded glassmorphic-panel">
          <div className="pos-toolbar-main d-flex align-items-center justify-content-between gap-3 flex-wrap mb-3">
            {/* Search Input supporting name, manufacturer, batch, category */}
            <div className="pos-search-wrapper flex-grow-1">
              <InputGroup className="pos-search-group">
                <InputGroup.Text className="bg-transparent border-0 pe-1 text-muted">
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by medicine name, manufacturer brand, batch number, or category..."
                  value={inputValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    setInputValue(value);
                    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
                    debounceTimeout.current = setTimeout(() => {
                      setSearchTerm(value);
                      setCurrentPage(1);
                    }, 350);
                  }}
                  className="pos-search-input border-0 ps-1"
                />
                {inputValue && (
                  <Button 
                    variant="link" 
                    onClick={() => { setInputValue(''); setSearchTerm(''); setCurrentPage(1); }}
                    className="pos-clear-search text-muted border-0"
                    title="Clear search"
                  >
                    <FaTimes />
                  </Button>
                )}
              </InputGroup>
            </div>

            {/* Action & Filter Controls Cluster */}
            <div className="pos-controls-cluster d-flex align-items-center gap-2 flex-wrap">
              {/* Sort Selector */}
              <div className="pos-select-wrapper">
                <Form.Select 
                  value={sortBy} 
                  onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
                  className="pos-select-pill"
                  title="Sort Catalog"
                >
                  <option value="most_ordered">🔥 Most Ordered</option>
                  <option value="name_asc">🔤 Name (A - Z)</option>
                  <option value="name_desc">🔤 Name (Z - A)</option>
                  <option value="stock_desc">📦 Highest Stock</option>
                  <option value="newest">🆕 Newest Added</option>
                </Form.Select>
              </div>

              {/* Stock Filter */}
              <div className="pos-select-wrapper">
                <Form.Select 
                  value={stockFilter} 
                  onChange={(e) => setStockFilter(e.target.value)}
                  className="pos-select-pill"
                  title="Filter by Stock Availability"
                >
                  <option value="all">📦 All Stocks</option>
                  <option value="in_stock">✅ In Stock Only</option>
                  <option value="low_stock">⚠️ Low Stock (&lt;50)</option>
                </Form.Select>
              </div>

              {/* View Switcher Segmented Button */}
              <div className="pos-segmented-switch">
                <button 
                  type="button" 
                  className={`switch-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  title="Grid Card View"
                >
                  <FaThLarge />
                </button>
                <button 
                  type="button" 
                  className={`switch-btn ${viewMode === 'table' ? 'active' : ''}`}
                  onClick={() => setViewMode('table')}
                  title="Table List View"
                >
                  <FaList />
                </button>
              </div>
            </div>
          </div>

          {/* Category Filter Chips Strip (Loaded from Database) */}
          {allCategories.length > 0 && (
            <div className="pos-category-strip pt-1">
              <div className="pos-category-chips-wrapper">
                <button
                  className={`pos-category-chip ${selectedCategory === 'all' ? 'active' : ''}`}
                  onClick={() => { setSelectedCategory('all'); setCurrentPage(1); }}
                >
                  <FaTag className="me-1 small" /> All Categories
                </button>
                {allCategories.map((cat, idx) => (
                  <button
                    key={idx}
                    className={`pos-category-chip ${selectedCategory.toLowerCase() === cat.toLowerCase() ? 'active' : ''}`}
                    onClick={() => { setSelectedCategory(cat); setCurrentPage(1); }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Catalog Content Area */}
        {error && <div className="text-danger p-3 bg-danger-subtle rounded mb-3">{error}</div>}

        {displayedBatches.length === 0 && !loading ? (
          <div className="pos-no-data-box py-5 text-center rounded glassmorphic-panel">
            <NoDataCard 
              title="No Medicines Found"
              message={searchTerm ? `No medicine matched "${searchTerm}". Try checking for typos or searching by brand/batch/category.` : "No medicine matches your current category or stock filter."}
              icon="💊"
              actionButton={
                <Button 
                  variant="primary" 
                  size="sm" 
                  onClick={() => { setInputValue(''); setSearchTerm(''); setSelectedCategory('all'); setStockFilter('all'); setCurrentPage(1); }}
                >
                  Reset All Filters
                </Button>
              }
            />
          </div>
        ) : viewMode === 'grid' ? (
          /* Grid Tiles View */
          <div className="pos-product-grid">
            {displayedBatches.map((batch) => {
              const activeUnit = selectedUnits[batch.batch_id] || 'piece';
              let activeFactor = 1;
              if (activeUnit === 'strip') activeFactor = batch.strip_conversion || 1;
              else if (activeUnit === 'box') activeFactor = batch.box_conversion || 1;

              const packageStock = Math.floor(batch.stock_quantity / activeFactor);
              const unitPrice = parseFloat(batch.selling_price || 0) * activeFactor;
              const isOutOfStock = packageStock <= 0;
              const currentQty = quantities[batch.batch_id] !== undefined ? quantities[batch.batch_id] : (isOutOfStock ? 0 : 1);
              const numericQty = Number(currentQty) || (isOutOfStock ? 0 : 1);

              const pieceStock = batch.stock_quantity;
              const stripStock = batch.strip_conversion > 1 ? Math.floor(batch.stock_quantity / batch.strip_conversion) : 0;
              const boxStock = batch.box_conversion > 1 ? Math.floor(batch.stock_quantity / batch.box_conversion) : 0;

              return (
                <div 
                  key={batch.batch_id} 
                  role="article" 
                  className={`pos-product-card ${isOutOfStock ? 'is-out-of-stock' : ''}`}
                  style={{ flexDirection: window.innerWidth <= 576 ? 'row' : 'column' }}
                >
                  {/* Top Row: Category, Popularity, Rx Badge & Expiry */}
                  <div className="card-top-tags d-flex justify-content-between align-items-center mb-2 flex-wrap gap-1">
                    <div className="d-flex align-items-center gap-1 flex-wrap">
                      <span className="card-category-pill text-truncate">
                        {batch.categories && batch.categories.length > 0 ? (batch.categories[0].name || batch.categories[0]) : 'Medicine'}
                      </span>
                      {(Boolean(batch.prescription_required) || Boolean(batch.requires_prescription) || Boolean(batch.prescriptionRequired)) && (
                        <span className="rx-required-badge" title="Prescription Required">
                          <FaFilePrescription size={11} /> Rx Required
                        </span>
                      )}
                      {batch.total_sold > 0 && (
                        <span className="card-popular-pill" title={`${batch.total_sold} units sold across orders`}>
                          <FaFire className="me-1 text-warning" />{batch.total_sold} sold
                        </span>
                      )}
                    </div>
                    {getExpiryBadge(batch.expiry_date)}
                  </div>

                  {/* Title, Manufacturer & Batch */}
                  <div className="card-medicine-info mb-2">
                    <h5 className="medicine-title" title={batch.medicine_name}>
                      {batch.medicine_name}
                    </h5>
                    <div className="medicine-manufacturer">
                      <span className="fw-semibold text-primary">{batch.manufacturer || 'General Pharma'}</span> • Batch: <span className="batch-code">{batch.batch_number}</span>
                    </div>
                  </div>

                  {/* Unit Selector & Stock indicator */}
                  <div className="pos-unit-stock-row mb-3 d-flex justify-content-between align-items-center">
                    <div className="unit-selector-wrapper">
                      <Form.Select
                        size="sm"
                        value={activeUnit}
                        onChange={(e) => {
                          const newUnit = e.target.value;
                          setSelectedUnits(prev => ({ ...prev, [batch.batch_id]: newUnit }));
                          let f = 1;
                          if (newUnit === 'strip') f = batch.strip_conversion || 1;
                          else if (newUnit === 'box') f = batch.box_conversion || 1;
                          const newPackStock = Math.floor(batch.stock_quantity / f);
                          setQuantities(prev => ({ ...prev, [batch.batch_id]: newPackStock > 0 ? 1 : 0 }));
                        }}
                        className="pos-unit-select"
                      >
                        <option value="piece">{batch.unit_name || 'Piece'} ({pieceStock} pcs)</option>
                        {batch.strip_conversion > 1 && (
                          <option value="strip">
                            Strip ({batch.strip_conversion}) {stripStock <= 0 ? `(Low: ${pieceStock} pcs)` : `(${stripStock} left)`}
                          </option>
                        )}
                        {batch.box_conversion > 1 && (
                          <option value="box">
                            Box ({batch.box_conversion}) {boxStock <= 0 ? `(Low: ${pieceStock} pcs)` : `(${boxStock} left)`}
                          </option>
                        )}
                      </Form.Select>
                    </div>

                    <div className="stock-status-wrapper text-end">
                      {isOutOfStock ? (
                        <span className="stock-badge stock-out" title={`Requires ${activeFactor} base units, only ${batch.stock_quantity} available`}>
                          {batch.stock_quantity > 0 ? `Needs ${activeFactor} (Has ${batch.stock_quantity})` : 'Out of Stock'}
                        </span>
                      ) : batch.stock_quantity < 50 ? (
                        <span className="stock-badge stock-low" title={`Low stock alert: Only ${batch.stock_quantity} base pieces left in stock`}>
                          ⚠️ Low: {packageStock} {activeUnit}s ({batch.stock_quantity} pcs)
                        </span>
                      ) : (
                        <span className="stock-badge stock-ok">{packageStock} {activeUnit}s left</span>
                      )}
                    </div>
                  </div>

                  {/* Price & Subtotal Row */}
                  <div className="pos-card-pricing-row mt-auto pt-2 mb-2 d-flex justify-content-between align-items-center">
                    <div className="pos-card-price">
                      <span className="price-symbol">{currencySymbol}</span>
                      <span className="price-val">{unitPrice.toFixed(2)}</span>
                      <span className="price-unit">/{activeUnit}</span>
                    </div>
                    {!isOutOfStock && numericQty > 1 && (
                      <div className="pos-card-subtotal-tag">
                        Total: {currencySymbol}{(unitPrice * numericQty).toFixed(2)}
                      </div>
                    )}
                  </div>

                  {/* Quantity Stepper & Add to Cart Action Row */}
                  <div className="pos-card-action-row d-flex align-items-center gap-2">
                    <div className="pos-qty-stepper flex-shrink-0">
                      <button 
                        type="button" 
                        className="qty-btn" 
                        onClick={() => handleDecrement(batch)}
                        disabled={isOutOfStock || numericQty <= 1}
                        title="Decrease Quantity"
                      >
                        <FaMinus />
                      </button>
                      <input
                        type="number"
                        min={isOutOfStock ? 0 : 1}
                        max={packageStock}
                        value={currentQty}
                        onChange={(e) => handleQuantityChange(batch, e.target.value)}
                        onBlur={() => {
                          if (isOutOfStock) {
                            setQuantities(prev => ({ ...prev, [batch.batch_id]: 0 }));
                          } else if (!quantities[batch.batch_id] || Number(quantities[batch.batch_id]) < 1) {
                            setQuantities(prev => ({ ...prev, [batch.batch_id]: 1 }));
                          }
                        }}
                        className="qty-input"
                        disabled={isOutOfStock}
                        title="Type quantity"
                      />
                      <button 
                        type="button" 
                        className="qty-btn" 
                        onClick={() => handleIncrement(batch)}
                        disabled={isOutOfStock || numericQty >= packageStock}
                        title="Increase Quantity"
                      >
                        <FaPlus />
                      </button>
                    </div>

                    <Button
                      variant={isOutOfStock ? "secondary" : "primary"}
                      size="sm"
                      onClick={() => handleAddToCart(batch, numericQty)}
                      disabled={isOutOfStock || numericQty < 1}
                      className="pos-add-btn flex-grow-1 d-flex align-items-center justify-content-center gap-1"
                      title={isOutOfStock ? `Requires ${activeFactor} base units, only ${batch.stock_quantity} available` : "Add to Cart"}
                    >
                      {isOutOfStock ? "Insufficient Stock" : <><FaShoppingCart /> Add to Cart</>}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* High-Density POS Table View */
          <div className="pos-table-wrapper rounded glassmorphic-panel overflow-hidden">
            <Table responsive hover className="pos-data-table mb-0 align-middle">
              <thead>
                <tr>
                  <th>Medicine & Manufacturer</th>
                  <th>Orders & Sold</th>
                  <th>Batch</th>
                  <th>Expiry</th>
                  <th>Unit Type</th>
                  <th>Price</th>
                  <th>Available Stock</th>
                  <th className="text-center" style={{ width: '130px' }}>Qty</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {displayedBatches.map((batch) => {
                  const activeUnit = selectedUnits[batch.batch_id] || 'piece';
                  let activeFactor = 1;
                  if (activeUnit === 'strip') activeFactor = batch.strip_conversion || 1;
                  else if (activeUnit === 'box') activeFactor = batch.box_conversion || 1;

                  const packageStock = Math.floor(batch.stock_quantity / activeFactor);
                  const unitPrice = parseFloat(batch.selling_price || 0) * activeFactor;
                  const isOutOfStock = packageStock <= 0;
                  const currentQty = quantities[batch.batch_id] !== undefined ? quantities[batch.batch_id] : (isOutOfStock ? 0 : 1);
                  const numericQty = Number(currentQty) || (isOutOfStock ? 0 : 1);

                  const pieceStock = batch.stock_quantity;
                  const stripStock = batch.strip_conversion > 1 ? Math.floor(batch.stock_quantity / batch.strip_conversion) : 0;
                  const boxStock = batch.box_conversion > 1 ? Math.floor(batch.stock_quantity / batch.box_conversion) : 0;

                  return (
                    <tr key={batch.batch_id} className={isOutOfStock ? 'table-row-out-of-stock' : ''}>
                      <td>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span className="table-med-name">{batch.medicine_name}</span>
                          {(Boolean(batch.prescription_required) || Boolean(batch.requires_prescription) || Boolean(batch.prescriptionRequired)) && (
                            <span className="rx-required-badge" style={{ fontSize: '0.65rem', padding: '1px 5px' }} title="Prescription Required">
                              <FaFilePrescription size={10} /> Rx Required
                            </span>
                          )}
                        </div>
                        <div className="table-med-meta text-primary fw-semibold d-flex align-items-center gap-2 flex-wrap">
                            <span>{batch.manufacturer || 'General'}</span>
                            {batch.rack_locations && batch.rack_locations.length > 0 && (
                              <span className="badge bg-light text-dark border table-rack-badge" style={{ fontSize: '0.7rem' }}>
                                <FaMapMarkerAlt className="text-danger me-1" size={9} />
                                {batch.rack_locations[0].rack_name} - {batch.rack_locations[0].slot_code}
                              </span>
                            )}
                          </div>
                      </td>
                      <td>
                        {batch.total_sold > 0 ? (
                          <span className="card-popular-pill" title={`${batch.total_orders} orders (${batch.total_sold} units sold)`}>
                            <FaFire className="me-1 text-warning" /> {batch.total_sold} sold
                          </span>
                        ) : (
                          <span className="text-muted small">0 sold</span>
                        )}
                      </td>
                      <td>
                        <span className="batch-code-pill">#{batch.batch_number}</span>
                      </td>
                      <td>{getExpiryBadge(batch.expiry_date)}</td>
                      <td>
                        <Form.Select
                          size="sm"
                          value={activeUnit}
                          onChange={(e) => {
                            const newUnit = e.target.value;
                            setSelectedUnits(prev => ({ ...prev, [batch.batch_id]: newUnit }));
                            let f = 1;
                            if (newUnit === 'strip') f = batch.strip_conversion || 1;
                            else if (newUnit === 'box') f = batch.box_conversion || 1;
                            const newPackStock = Math.floor(batch.stock_quantity / f);
                            setQuantities(prev => ({ ...prev, [batch.batch_id]: newPackStock > 0 ? 1 : 0 }));
                          }}
                          className="pos-unit-select-table"
                        >
                          <option value="piece">{batch.unit_name || 'Piece'} ({pieceStock} pcs)</option>
                          {batch.strip_conversion > 1 && (
                            <option value="strip">
                              Strip ({batch.strip_conversion}) {stripStock <= 0 ? `(Low: ${pieceStock} pcs)` : `(${stripStock} left)`}
                            </option>
                          )}
                          {batch.box_conversion > 1 && (
                            <option value="box">
                              Box ({batch.box_conversion}) {boxStock <= 0 ? `(Low: ${pieceStock} pcs)` : `(${boxStock} left)`}
                            </option>
                          )}
                        </Form.Select>
                      </td>
                      <td>
                        <span className="table-price">{currencySymbol}{unitPrice.toFixed(2)}</span>
                      </td>
                      <td>
                        {isOutOfStock ? (
                          <span className="stock-badge stock-out" title={`Requires ${activeFactor} base units, only ${batch.stock_quantity} available`}>
                            {batch.stock_quantity > 0 ? `Needs ${activeFactor} (${batch.stock_quantity} left)` : 'Out of Stock'}
                          </span>
                        ) : batch.stock_quantity < 50 ? (
                          <span className="stock-badge stock-low" title={`Low stock alert: Only ${batch.stock_quantity} base pieces left`}>
                            ⚠️ Low ({batch.stock_quantity} pcs)
                          </span>
                        ) : (
                          <span className="stock-badge stock-ok">{packageStock} {activeUnit}s</span>
                        )}
                      </td>
                      <td>
                        <div className="pos-qty-stepper mx-auto">
                          <button 
                            type="button" 
                            className="qty-btn" 
                            onClick={() => handleDecrement(batch)}
                            disabled={isOutOfStock || numericQty <= 1}
                          >
                            <FaMinus />
                          </button>
                          <input
                            type="number"
                            min={isOutOfStock ? 0 : 1}
                            max={packageStock}
                            value={currentQty}
                            onChange={(e) => handleQuantityChange(batch, e.target.value)}
                            onBlur={() => {
                              if (isOutOfStock) {
                                setQuantities(prev => ({ ...prev, [batch.batch_id]: 0 }));
                              } else if (!quantities[batch.batch_id] || Number(quantities[batch.batch_id]) < 1) {
                                setQuantities(prev => ({ ...prev, [batch.batch_id]: 1 }));
                              }
                            }}
                            className="qty-input"
                            disabled={isOutOfStock}
                          />
                          <button 
                            type="button" 
                            className="qty-btn" 
                            onClick={() => handleIncrement(batch)}
                            disabled={isOutOfStock || numericQty >= packageStock}
                          >
                            <FaPlus />
                          </button>
                        </div>
                      </td>
                      <td className="text-end">
                        <Button
                          variant={isOutOfStock ? "secondary" : "primary"}
                          size="sm"
                          onClick={() => handleAddToCart(batch, numericQty)}
                          disabled={isOutOfStock || numericQty < 1}
                          className="pos-table-add-btn"
                        >
                          {isOutOfStock ? "Unavailable" : <><FaPlus className="me-1" /> Add</>}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}

        {/* Dynamic Pagination Bar directly beneath Medicine List */}
        {renderPagination()}
      </div>

      {/* Full-Width Section 2: Active Register & Billing Terminal (Docked at the Bottom) */}
      <div className="pos-bottom-register-hub pt-2" ref={registerRef}>
        <div className="pos-register-main-card p-4 rounded glassmorphic-panel">
          {/* Register Card Master Title */}
          <div className="register-master-header d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <div className="register-header-icon">
                <FaReceipt />
              </div>
              <div>
                <h4 className="register-title-main mb-0">Active Register & Checkout Hub</h4>
                <p className="register-subtitle-main mb-0 text-muted small">Finalize customer billing, apply prescription details, and collect payment.</p>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Badge bg="primary" pill className="px-3 py-2 fs-6">
                {cartTotalItems} {cartTotalItems === 1 ? 'item' : 'items'} in cart
              </Badge>
              {cart.items.length > 0 && (
                <Button variant="outline-danger" size="sm" onClick={clearCart} className="clear-register-btn">
                  <FaTrash className="me-1" /> Empty Cart
                </Button>
              )}
            </div>
          </div>

          <Row className="g-4">
            {/* Column 1: Live Cart Items Manager */}
            <Col xs={12} lg={5}>
              <div className="register-sub-panel p-3 rounded h-100 d-flex flex-column">
                <div className="sub-panel-header d-flex justify-content-between align-items-center mb-3">
                  <span className="sub-panel-title fw-bold d-flex align-items-center gap-2">
                    <FaShoppingCart className="text-primary" /> Billed Items ({cart.items.length})
                  </span>
                  <span className="sub-panel-badge small text-muted">
                    Total: {currencySymbol}{cartTotal.toFixed(2)}
                  </span>
                </div>

                <div className="pos-cart-items-scrollable flex-grow-1">
                  {cart.items.length === 0 ? (
                    <div className="empty-cart-state py-5 text-center">
                      <div className="empty-cart-icon mb-2">
                        <FaShoppingCart />
                      </div>
                      <h6 className="text-white fw-bold mb-1">Active Register is Empty</h6>
                      <p className="text-muted small mb-0">Select medicines from the catalog above to build customer invoice.</p>
                    </div>
                  ) : (
                    <div className="pos-cart-list">
                      {cart.items.map((item) => (
                        <div key={item.id} className="pos-cart-item-row d-flex justify-content-between align-items-center p-3 mb-2 rounded">
                          <div className="cart-item-info pe-2">
                            <div className="cart-item-name fw-semibold text-truncate d-flex align-items-center gap-1 flex-wrap">
                              <span>{item.name}</span>
                              {item.rack_location && (
                                <span className="badge bg-light text-dark border extra-small cart-item-rack-badge" style={{ fontSize: '0.68rem' }}>
                                  <FaMapMarkerAlt className="text-danger me-1" size={8} />
                                  {item.rack_location}
                                </span>
                              )}
                            </div>
                            <div className="cart-item-subtext small d-flex align-items-center gap-1 flex-wrap">
                              <Form.Select
                                size="sm"
                                value={item.selected_unit || 'piece'}
                                onChange={(e) => handleCartItemUnitChange(item, e.target.value)}
                                className="pos-cart-unit-select py-0"
                                style={{ fontSize: '0.75rem', width: 'auto', paddingLeft: '5px', paddingRight: '20px', height: '22px' }}
                              >
                                <option value="piece">{(item.unit_name || 'piece').toUpperCase()}</option>
                                {item.strip_conversion > 1 && <option value="strip">STRIP ({item.strip_conversion})</option>}
                                {item.box_conversion > 1 && <option value="box">BOX ({item.box_conversion})</option>}
                              </Form.Select>
                              <span>• {currencySymbol}{parseFloat(item.price || 0).toFixed(2)} / unit</span>
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-2 flex-shrink-0">
                            <div className="pos-cart-qty-stepper">
                              <button 
                                type="button" 
                                className="cart-qty-btn"
                                onClick={() => decreaseQuantity(item.id)}
                                title="Decrease quantity"
                              >
                                <FaMinus />
                              </button>
                              <input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => handleUpdateCartQuantity(item, e.target.value)}
                                className="cart-qty-input"
                                title="Edit quantity"
                              />
                              <button 
                                type="button" 
                                className="cart-qty-btn"
                                onClick={() => handleIncreaseCartQuantity(item)}
                                title="Increase quantity"
                              >
                                <FaPlus />
                              </button>
                            </div>

                            <div className="cart-item-total fw-bold text-end" style={{ minWidth: '70px' }}>
                              {currencySymbol}{(parseFloat(item.price || 0) * item.quantity).toFixed(2)}
                            </div>

                            <button 
                              type="button" 
                              className="cart-remove-btn" 
                              onClick={() => removeFromCart(item.id)}
                              title="Remove item"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Col>

            {/* Column 2: Customer Desk & Rx Compliance */}
            <Col xs={12} lg={3}>
              <div className="register-sub-panel p-3 rounded h-100">
                <div className="sub-panel-header mb-3">
                  <span className="sub-panel-title fw-bold d-flex align-items-center gap-2">
                    <FaUser className="text-info" /> Customer & Compliance Desk
                  </span>
                </div>

                {/* Customer Mobile Auto-lookup */}
                <div className="customer-lookup-box p-3 rounded mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="customer-box-label small fw-bold">Customer Mobile <span className="text-danger">*</span></span>
                    {customerFound && <span className="badge bg-success-subtle text-success small"><FaCheck className="me-1" /> Registered</span>}
                  </div>
                  <InputGroup size="sm" className="mb-2">
                    <InputGroup.Text className="bg-transparent border-end-0">
                      <FaPhoneAlt className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                      type="tel"
                      placeholder="Enter 10-digit mobile"
                      value={customerMobile}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '');
                        setCustomerMobile(digits);
                        setCustomerFound(false);
                        setPreviousOrders([]);
                        if (mobileDebounce.current) clearTimeout(mobileDebounce.current);
                        mobileDebounce.current = setTimeout(async () => {
                          if (!digits || digits.length < 7) return;
                          
                          // 1. Search customer user profile
                          try {
                            setCustomerLookupLoading(true);
                            const res = await get(`/users/search/mobile/${digits}`);
                            if (res && res.success && res.data) {
                              setCustomerName(res.data.name || '');
                              setCustomerEmail(res.data.email || '');
                              setCustomerFound(true);
                              showToast(`Found registered customer: ${res.data.name}`, 'info');
                            }
                          } catch (err) {
                            // ignore lookup errors
                          } finally {
                            setCustomerLookupLoading(false);
                          }

                          // 2. Search previous orders (guest or registered)
                          if (digits.length >= 10) {
                            try {
                              setLoadingPreviousOrders(true);
                              const ordersRes = await get(`/orders?customer_mobile=${digits}&limit=10`);
                              if (ordersRes && ordersRes.success && Array.isArray(ordersRes.data)) {
                                setPreviousOrders(ordersRes.data);
                              }
                            } catch (ordersErr) {
                              // ignore order fetch errors
                            } finally {
                              setLoadingPreviousOrders(false);
                            }
                          }
                        }, 400);
                      }}
                      className="customer-mobile-input border-start-0"
                    />
                    {customerLookupLoading && (
                      <InputGroup.Text className="bg-transparent">
                        <Spinner animation="border" size="sm" />
                      </InputGroup.Text>
                    )}
                  </InputGroup>

                  <div className="customer-extra-inputs">
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder="Customer Name (Optional)"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="mb-2"
                    />
                    <Form.Control
                      size="sm"
                      type="email"
                      placeholder="Email for E-Bill (Optional)"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                    />
                  </div>
                  {previousOrders.length > 0 && (
                    <Button 
                      variant="outline-info" 
                      size="sm" 
                      className="w-100 mt-2 d-flex align-items-center justify-content-center gap-1"
                      onClick={() => setShowHistoryModal(true)}
                    >
                      <FaReceipt /> View History ({previousOrders.length} order{previousOrders.length > 1 ? 's' : ''})
                    </Button>
                  )}
                  {loadingPreviousOrders && (
                    <div className="text-center mt-2 small text-muted">
                      <Spinner animation="border" size="sm" className="me-1" style={{ width: '12px', height: '12px' }} /> Checking history...
                    </div>
                  )}
                </div>

                {/* Prescription Warning / Upload Box */}
                {requiresPrescription && (
                  <div className="prescription-compliance-box p-3 rounded mb-3 border border-warning bg-dark text-white">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="text-warning fw-bold small d-flex align-items-center gap-1">
                        <FaFilePrescription /> Scheduled Rx Medicine
                      </div>
                      <Badge bg="success" className="d-flex align-items-center gap-1">
                        <FaCheck size={10} /> Auto-Verified
                      </Badge>
                    </div>

                    {/* Show Found Existing Prescription */}
                    {fetchedPrescription ? (
                      <div className="position-relative border border-info rounded p-2 bg-black bg-opacity-50 mb-2">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          {fetchedPrescription.image_url ? (
                            <img 
                              src={fetchedPrescription.image_url.startsWith('http') ? fetchedPrescription.image_url : `${BASE_URL}${fetchedPrescription.image_url}`} 
                              alt="Prescription Thumbnail" 
                              style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer', border: '1px solid #0dcaf0' }}
                              onClick={() => {
                                setActivePrescriptionViewUrl(fetchedPrescription.image_url.startsWith('http') ? fetchedPrescription.image_url : `${BASE_URL}${fetchedPrescription.image_url}`);
                                setRxZoom(1);
                                setRxRotate(0);
                                setShowPrescriptionViewerModal(true);
                              }}
                            />
                          ) : (
                            <FaFilePrescription className="text-info" size={32} />
                          )}
                          <div className="flex-grow-1" style={{ overflow: 'hidden' }}>
                            <div className="d-flex align-items-center justify-content-between">
                              <span className="text-info small fw-bold">Rx #{fetchedPrescription.prescription_id}</span>
                              <Badge bg={fetchedPrescription.status === 'approved' ? 'success' : 'warning'} style={{ fontSize: '9px' }}>
                                {fetchedPrescription.status.toUpperCase()}
                              </Badge>
                            </div>
                            <div className="text-white small text-truncate" style={{ fontSize: '11px' }}>
                              {fetchedPrescription.customer_name || 'Walk-in Customer'}
                            </div>
                            {fetchedPrescription.doctor_name && (
                              <div className="text-muted" style={{ fontSize: '10px' }}>Dr. {fetchedPrescription.doctor_name}</div>
                            )}
                          </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center pt-1 border-top border-secondary">
                          {fetchedPrescription.image_url && (
                            <Button 
                              variant="link" 
                              size="sm" 
                              className="p-0 text-decoration-none text-info small"
                              style={{ fontSize: '11px' }}
                              onClick={() => {
                                setActivePrescriptionViewUrl(fetchedPrescription.image_url.startsWith('http') ? fetchedPrescription.image_url : `${BASE_URL}${fetchedPrescription.image_url}`);
                                setRxZoom(1);
                                setRxRotate(0);
                                setShowPrescriptionViewerModal(true);
                              }}
                            >
                              <FaEye className="me-1" /> View Image
                            </Button>
                          )}
                          <Button 
                            variant="outline-danger" 
                            size="sm" 
                            className="py-0 px-2"
                            style={{ fontSize: '10px' }}
                            onClick={handleClearFetchedPrescription}
                          >
                            Change
                          </Button>
                        </div>
                      </div>
                    ) : prescriptionPreview ? (
                      /* Show Uploaded Preview */
                      <div className="position-relative border border-secondary rounded p-2 text-center bg-black bg-opacity-50 mb-2">
                        <img 
                          src={prescriptionPreview} 
                          alt="Prescription" 
                          style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain', borderRadius: '4px', cursor: 'pointer' }} 
                          onClick={() => {
                            setActivePrescriptionViewUrl(prescriptionPreview);
                            setRxZoom(1);
                            setRxRotate(0);
                            setShowPrescriptionViewerModal(true);
                          }}
                        />
                        <div className="mt-1 d-flex align-items-center justify-content-between px-1">
                          <span className="text-success small fw-semibold">✓ Attached Photo</span>
                          <Button 
                            variant="outline-danger" 
                            size="sm" 
                            className="py-0 px-2"
                            style={{ fontSize: '11px' }}
                            onClick={handleRemovePrescription}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* Show Upload Dropzone */
                      <div 
                        className="border border-dashed border-secondary rounded p-3 text-center cursor-pointer mb-2"
                        onClick={() => document.getElementById('pos-register-prescription-input')?.click()}
                        style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)' }}
                      >
                        <FaFilePrescription className="text-warning mb-1" size={22} />
                        <div className="small fw-semibold text-white">Upload Prescription Photo *</div>
                        <div className="text-muted" style={{ fontSize: '11px' }}>Click to browse or take photo</div>
                        <input 
                          type="file" 
                          id="pos-register-prescription-input" 
                          accept="image/*" 
                          style={{ display: 'none' }} 
                          onChange={handlePrescriptionFileChange}
                        />
                      </div>
                    )}

                    {/* Prescription ID Search Field */}
                    <Form.Group className="mb-1">
                      <div className="position-relative">
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="Or search by Prescription ID..."
                          value={prescriptionId}
                          onChange={(e) => handlePrescriptionIdInput(e.target.value)}
                          className="mb-1 bg-dark text-white border-secondary"
                        />
                        {fetchingPrescription && (
                          <Spinner 
                            animation="border" 
                            size="sm" 
                            className="position-absolute text-info" 
                            style={{ right: '8px', top: '8px', width: '14px', height: '14px' }} 
                          />
                        )}
                      </div>
                      {prescriptionLookupError && (
                        <div className="text-danger small mt-1" style={{ fontSize: '11px' }}>
                          <FaExclamationTriangle className="me-1" /> {prescriptionLookupError}
                        </div>
                      )}
                    </Form.Group>
                  </div>
                )}

                {/* Counter Remarks */}
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted mb-1">Counter Sale Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    size="sm"
                    placeholder="Optional notes for this order..."
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    className="pos-remarks-textarea"
                  />
                </Form.Group>
              </div>
            </Col>

            {/* Column 3: Payment Settlement Card */}
            <Col xs={12} lg={4}>
              <div className="register-sub-panel p-3 rounded h-100 d-flex flex-column justify-content-between">
                <div>
                  <div className="sub-panel-header mb-3">
                    <span className="sub-panel-title fw-bold d-flex align-items-center gap-2">
                      <FaCreditCard className="text-success" /> Payment Settlement
                    </span>
                  </div>

                  {/* Payment Mode Selector */}
                  <div className="payment-method-selector mb-3">
                    <label className="payment-label small fw-bold mb-2 d-block text-muted">Select Payment Method</label>
                    <div className="payment-chips-grid">
                      {paymentMethods.map((pm) => (
                        <button
                          key={pm.id}
                          type="button"
                          className={`payment-chip ${paymentMethod === pm.id ? 'active' : ''}`}
                          onClick={() => setPaymentMethod(pm.id)}
                        >
                          {getPaymentIcon(pm.icon || pm.id)} {pm.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Financial Breakdown */}
                  <div className="register-financial-summary p-3 rounded mb-3">
                    <div className="d-flex justify-content-between mb-2 small text-muted">
                      <span>Taxable Subtotal:</span>
                      <span className="fw-semibold text-white">{currencySymbol}{cartSubtotal.toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-1 small text-muted">
                      <span>Total GST Tax:</span>
                      <span className="fw-semibold text-info">{currencySymbol}{cartTotalTax.toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2 small text-muted ps-2" style={{ fontSize: '0.75rem' }}>
                      <span>CGST ({halfGstRate}%): {currencySymbol}{(cartTotalTax / 2).toFixed(2)}</span>
                      <span>SGST ({halfGstRate}%): {currencySymbol}{(cartTotalTax / 2).toFixed(2)}</span>
                    </div>
                    <hr className="my-2 register-divider" />
                    <div className="d-flex justify-content-between align-items-center pt-1">
                      <span className="payable-label fw-bold fs-6">Grand Total:</span>
                      <span className="payable-amount fw-bold text-primary fs-4">
                        {currencySymbol}{cartTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Checkout CTA */}
                <Button
                  variant="primary"
                  size="lg"
                  className="w-100 py-3 pos-checkout-btn fw-bold d-flex align-items-center justify-content-center gap-2 shadow"
                  disabled={cart.items.length === 0 || loading}
                  onClick={() => setShowSellModal(true)}
                >
                  <FaCheckCircle /> Finalize & Complete Sale ({currencySymbol}{cartTotal.toFixed(2)})
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Floating Action Dock for Cart (Quick checkout access when scrolling) */}
      {cart.items.length > 0 && (
        <div className="pos-floating-action-bar p-3 rounded-pill glassmorphic-panel shadow-lg d-flex justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="floating-cart-badge" data-testid="cart-icon" onClick={() => setShowSellModal(true)} style={{ cursor: 'pointer' }}>
              <FaShoppingCart />
              <span className="badge bg-danger rounded-pill floating-qty">{cartTotalItems}</span>
            </div>
            <div>
              <div className="floating-cart-title small text-muted">Active Bill</div>
              <div className="floating-cart-price fw-bold text-white fs-5">{currencySymbol}{cartTotal.toFixed(2)}</div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Button 
              variant="outline-light" 
              size="sm" 
              className="rounded-pill px-3"
              onClick={() => {
                if (registerRef.current) {
                  registerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
            >
              <FaArrowDown className="me-1" /> View Register
            </Button>
            <Button 
              variant="primary" 
              size="sm" 
              className="rounded-pill px-4 fw-bold shadow-sm"
              onClick={() => setShowSellModal(true)}
            >
              <FaCheckCircle className="me-1" /> Pay Now
            </Button>
          </div>
        </div>
      )}

      {/* Confirmation & Complete Sale Modal */}
      <Modal 
        show={showSellModal} 
        onHide={() => setShowSellModal(false)} 
        centered 
        size="lg"
        scrollable
        className="pos-checkout-modal"
        dialogClassName="order-modal-dialog-lg"
        backdropClassName="order-modal-backdrop"
      >
        <Modal.Header closeButton className="order-modal-header py-3 px-4">
          <div className="d-flex align-items-center gap-3">
            <div className="order-modal-icon-badge">
              <FaCoins />
            </div>
            <div>
              <Modal.Title className="modal-order-title mb-0">Confirm Point of Sale Bill</Modal.Title>
              <div className="modal-subtitle text-muted small">Review items, customer info, and finalize receipt</div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="order-modal-body p-4">
          <div className="order-modal-layout">
            {/* Left Column: Items Table & Total */}
            <div className="order-modal-left">
              <div className="order-items-card p-3 rounded mb-3">
                <h6 className="section-title mb-3 d-flex align-items-center gap-2">
                  <FaShoppingCart className="text-primary" /> Invoice Items ({cart.items.length})
                </h6>
                <div className="ordered-items-list">
                  {cart.items.map((item, idx) => (
                    <div key={idx} className="ordered-item-row">
                      <div className="item-info">
                        <div className="item-name">
                          {item.name}
                          <span className="item-qty-badge ms-2">×{item.quantity}</span>
                        </div>
                        <div className="item-meta-line">
                          <span className="item-category-tag">{item.display_unit || 'Piece'}</span>
                          <span className="item-meta-separator">•</span>
                          <span className="item-unit-price">Unit: {currencySymbol}{parseFloat(item.price || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="item-subtotal-value">
                        {currencySymbol}{(parseFloat(item.price || 0) * item.quantity).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="order-summary-box p-3 rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="summary-field-label">Payment Mode:</span>
                  <span className="summary-field-value text-uppercase">{paymentMethods.find(m => m.id === paymentMethod)?.name || paymentMethod}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="summary-field-label">Total Quantity:</span>
                  <span className="summary-field-value">{cartTotalItems} units</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-1 small text-muted">
                  <span className="summary-field-label">Taxable Subtotal:</span>
                  <span className="summary-field-value">{currencySymbol}{cartSubtotal.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-1 small text-muted">
                  <span className="summary-field-label">Total GST Tax:</span>
                  <span className="summary-field-value text-info">{currencySymbol}{cartTotalTax.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2 small text-muted ps-2" style={{ fontSize: '0.75rem' }}>
                  <span>CGST ({halfGstRate}%): {currencySymbol}{(cartTotalTax / 2).toFixed(2)}</span>
                  <span>SGST ({halfGstRate}%): {currencySymbol}{(cartTotalTax / 2).toFixed(2)}</span>
                </div>
                <hr className="my-2 summary-box-divider" />
                <div className="d-flex justify-content-between align-items-center pt-1">
                  <span className="summary-total-label">Grand Total:</span>
                  <span className="summary-total-value text-primary">
                    {currencySymbol}{cartTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Right Column: Customer Info, Payment & Prescription */}
            <div className="order-modal-right">
              <div className="status-update-panel p-3 rounded mb-3">
                <h6 className="section-title mb-3 d-flex align-items-center gap-2">
                  <FaUser className="text-info" /> Customer & Billing Info
                </h6>
                <Form>
                  <Form.Group className="mb-2">
                    <Form.Label className="status-form-label mb-1">Customer Mobile <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="e.g. 9876543210"
                      value={customerMobile}
                      onChange={(e) => setCustomerMobile(e.target.value.replace(/\D/g, ''))}
                      className="status-select-input"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label className="status-form-label mb-1">Customer Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Customer Name"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="status-select-input"
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label className="status-form-label mb-1">Email (For Digital Bill)</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="email@domain.com (optional)"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      className="status-select-input"
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label className="status-form-label mb-1">Payment Method</Form.Label>
                    <Form.Select 
                      value={paymentMethod} 
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="status-select-input"
                    >
                      {paymentMethods.map(pm => (
                        <option key={pm.id} value={pm.id}>{pm.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label className="status-form-label mb-1">Remarks / Note</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Counter sale notes (optional)"
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      className="status-select-input"
                    />
                  </Form.Group>

                  {/* Prescription Section if needed */}
                  {requiresPrescription && (
                    <div className="mt-3 p-3 rounded border border-warning bg-dark">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="text-warning fw-bold small d-flex align-items-center gap-1">
                          <FaExclamationTriangle /> Prescription Required
                        </div>
                        <Badge bg="success" className="d-flex align-items-center gap-1">
                          <FaCheck size={10} /> Auto-Verified In-Store
                        </Badge>
                      </div>

                      {/* Attached Existing Prescription Card */}
                      {fetchedPrescription ? (
                        <div className="position-relative border border-info rounded p-2 bg-black bg-opacity-50 mb-3">
                          <div className="d-flex align-items-center gap-3 mb-2">
                            {fetchedPrescription.image_url ? (
                              <img 
                                src={fetchedPrescription.image_url.startsWith('http') ? fetchedPrescription.image_url : `${BASE_URL}${fetchedPrescription.image_url}`} 
                                alt="Prescription" 
                                style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer', border: '2px solid #0dcaf0' }} 
                                onClick={() => {
                                  setActivePrescriptionViewUrl(fetchedPrescription.image_url.startsWith('http') ? fetchedPrescription.image_url : `${BASE_URL}${fetchedPrescription.image_url}`);
                                  setRxZoom(1);
                                  setRxRotate(0);
                                  setShowPrescriptionViewerModal(true);
                                }}
                              />
                            ) : (
                              <FaFilePrescription className="text-info" size={40} />
                            )}
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="text-info fw-bold small">Rx #{fetchedPrescription.prescription_id}</span>
                                <Badge bg={fetchedPrescription.status === 'approved' ? 'success' : 'warning'}>
                                  {fetchedPrescription.status.toUpperCase()}
                                </Badge>
                              </div>
                              <div className="text-white small">{fetchedPrescription.customer_name || 'Walk-in Customer'}</div>
                              {fetchedPrescription.doctor_name && (
                                <div className="text-muted small" style={{ fontSize: '11px' }}>
                                  Doctor: Dr. {fetchedPrescription.doctor_name}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="d-flex justify-content-between align-items-center pt-2 border-top border-secondary">
                            {fetchedPrescription.image_url && (
                              <Button 
                                variant="outline-info" 
                                size="sm" 
                                className="py-0 px-2"
                                style={{ fontSize: '11px' }}
                                onClick={() => {
                                  setActivePrescriptionViewUrl(fetchedPrescription.image_url.startsWith('http') ? fetchedPrescription.image_url : `${BASE_URL}${fetchedPrescription.image_url}`);
                                  setRxZoom(1);
                                  setRxRotate(0);
                                  setShowPrescriptionViewerModal(true);
                                }}
                              >
                                <FaEye className="me-1" /> View Full Prescription
                              </Button>
                            )}
                            <Button 
                              variant="outline-danger" 
                              size="sm" 
                              className="py-0 px-2"
                              style={{ fontSize: '11px' }}
                              onClick={handleClearFetchedPrescription}
                            >
                              Change Prescription
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* File Upload Box */
                        <Form.Group className="mb-3">
                          <Form.Label className="status-form-label mb-1 text-white d-flex align-items-center gap-1">
                            <FaFilePrescription className="text-warning" /> Upload Customer Prescription <span className="text-danger">*</span>
                          </Form.Label>

                          {prescriptionPreview ? (
                            <div className="position-relative border border-secondary rounded p-2 text-center bg-black bg-opacity-50">
                              <img 
                                src={prescriptionPreview} 
                                alt="Prescription Preview" 
                                style={{ maxHeight: '140px', maxWidth: '100%', objectFit: 'contain', borderRadius: '6px', cursor: 'pointer' }} 
                                onClick={() => {
                                  setActivePrescriptionViewUrl(prescriptionPreview);
                                  setRxZoom(1);
                                  setRxRotate(0);
                                  setShowPrescriptionViewerModal(true);
                                }}
                              />
                              <div className="mt-2 d-flex align-items-center justify-content-between px-2">
                                <span className="text-success small fw-semibold d-flex align-items-center gap-1">
                                  <FaCheckCircle /> Prescription Attached
                                </span>
                                <Button 
                                  variant="outline-danger" 
                                  size="sm" 
                                  className="py-0 px-2"
                                  onClick={handleRemovePrescription}
                                >
                                  <FaTrash className="me-1" /> Remove
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div 
                              className="border border-dashed border-secondary rounded p-3 text-center cursor-pointer"
                              onClick={() => document.getElementById('pos-prescription-upload-input')?.click()}
                              style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.03)' }}
                            >
                              <FaFilePrescription className="text-warning mb-2" size={28} />
                              <div className="text-white small fw-semibold">Click to upload prescription photo</div>
                              <div className="text-muted" style={{ fontSize: '11px' }}>Supports JPG, PNG, WebP</div>
                              <input 
                                type="file" 
                                id="pos-prescription-upload-input" 
                                accept="image/*" 
                                style={{ display: 'none' }} 
                                onChange={handlePrescriptionFileChange}
                              />
                            </div>
                          )}
                        </Form.Group>
                      )}

                      {/* Optional Doctor Information Toggle / Inputs */}
                      <Accordion className="border-0 bg-transparent mt-2">
                        <Accordion.Item eventKey="0" className="border-0 bg-transparent">
                          <Accordion.Header className="text-muted small">
                            <span style={{ fontSize: '12px', color: '#adb5bd' }}>+ Search by Prescription ID / Doctor Details</span>
                          </Accordion.Header>
                          <Accordion.Body className="p-0 pt-2">
                            <Form.Group className="mb-2">
                              <Form.Label className="status-form-label mb-1">Prescription ID on file</Form.Label>
                              <div className="position-relative">
                                <Form.Control
                                  type="text"
                                  placeholder="Enter Prescription ID..."
                                  value={prescriptionId}
                                  onChange={(e) => handlePrescriptionIdInput(e.target.value)}
                                  className="status-select-input"
                                />
                                {fetchingPrescription && (
                                  <Spinner 
                                    animation="border" 
                                    size="sm" 
                                    className="position-absolute text-info" 
                                    style={{ right: '12px', top: '10px', width: '16px', height: '16px' }} 
                                  />
                                )}
                              </div>
                              {prescriptionLookupError && (
                                <div className="text-danger small mt-1" style={{ fontSize: '11px' }}>
                                  <FaExclamationTriangle className="me-1" /> {prescriptionLookupError}
                                </div>
                              )}
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Label className="status-form-label mb-1">Doctor Name</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Dr. Full Name"
                                value={doctorName}
                                onChange={(e) => setDoctorName(e.target.value)}
                                className="status-select-input"
                              />
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Label className="status-form-label mb-1">Doctor Phone</Form.Label>
                              <Form.Control
                                type="tel"
                                placeholder="Doctor Contact Number"
                                value={doctorPhone}
                                onChange={(e) => setDoctorPhone(e.target.value.replace(/\D/g, ''))}
                                className="status-select-input"
                              />
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Label className="status-form-label mb-1">Doctor Registration #</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Medical Registration Number"
                                value={doctorRegistrationNumber}
                                onChange={(e) => setDoctorRegistrationNumber(e.target.value)}
                                className="status-select-input"
                              />
                            </Form.Group>
                          </Accordion.Body>
                        </Accordion.Item>
                      </Accordion>
                    </div>
                  )}
                </Form>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="order-modal-footer d-flex justify-content-between align-items-center py-2 px-4">
          <Button variant="secondary" onClick={() => setShowSellModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSellConfirm} 
            disabled={loading}
            className="py-2 px-4 d-flex align-items-center gap-2 fw-bold"
          >
            {loading ? <><Spinner animation="border" size="sm" /> Processing Sale...</> : <><FaCheckCircle /> Confirm & Complete Sale</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Send Email Prompt Modal */}
      <Modal 
        show={showSendEmailPrompt} 
        onHide={() => setShowSendEmailPrompt(false)} 
        centered
        className="pos-checkout-modal pos-email-prompt-modal"
        backdropClassName="order-modal-backdrop"
      >
        <Modal.Header closeButton className="order-modal-header py-3 px-4">
          <Modal.Title className="modal-order-title fs-5 d-flex align-items-center gap-2">
            <FaEnvelope className="text-primary" /> Send Invoice to Customer
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="email-prompt-card p-3 rounded mb-1">
            <div className="email-prompt-target mb-2">
              Customer Email: <strong className="text-primary text-break">{customerEmail || '—'}</strong>
            </div>
            <div className="email-prompt-question">
              Would you like to send a digital copy of the bill to the customer's email address?
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="order-modal-footer d-flex justify-content-between align-items-center py-2 px-4">
          <Button variant="secondary" size="sm" onClick={() => setShowSendEmailPrompt(false)}>
            No, Skip
          </Button>
          <Button 
            variant="primary" 
            size="sm" 
            onClick={handleSendBillEmail} 
            disabled={sendingEmail}
            className="d-flex align-items-center gap-2 fw-bold"
          >
            {sendingEmail ? <><Spinner animation="border" size="sm" /> Sending...</> : <><FaEnvelope /> Send Bill Email</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Previous Orders History Modal */}
      <Modal 
        show={showHistoryModal} 
        onHide={() => setShowHistoryModal(false)} 
        size="lg" 
        centered
        className="previous-orders-modal"
      >
        <Modal.Header closeButton className="bg-info-subtle border-0 py-3 px-4">
          <Modal.Title className="fs-5 d-flex align-items-center gap-2 text-info-emphasis fw-bold">
            <FaReceipt /> Purchase History: {customerName || 'Walk-in Customer'} ({customerMobile})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {previousOrders.length === 0 ? (
            <div className="text-center p-5 text-muted">
              <FaReceipt size={40} className="mb-2 text-secondary opacity-50" />
              <p className="mb-0">No previous orders found for this customer mobile.</p>
            </div>
          ) : (
            <Accordion flush defaultActiveKey="0">
              {previousOrders.map((order, index) => {
                const dateStr = new Date(order.order_date).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });
                
                // Color badges based on status
                let statusVariant = 'secondary';
                if (order.status === 'delivered' || order.status === 'COMPLETED') statusVariant = 'success';
                else if (order.status === 'confirmed' || order.status === 'CONFIRMED') statusVariant = 'info';
                else if (order.status === 'pending' || order.status === 'PENDING') statusVariant = 'warning';
                else if (order.status === 'cancelled' || order.status === 'CANCELLED') statusVariant = 'danger';

                return (
                  <Accordion.Item eventKey={String(index)} key={order.order_id}>
                    <Accordion.Header>
                      <div className="d-flex flex-wrap align-items-center gap-3 w-100 justify-content-between me-3">
                        <span className="fw-bold">Order #{order.order_id}</span>
                        <span className="text-muted small">{dateStr}</span>
                        <span>
                          Type: <Badge bg="secondary" className="text-uppercase">{order.order_type}</Badge>
                        </span>
                        <span>
                          Total: <span className="fw-bold text-success">{currencySymbol}{parseFloat(order.total_amount).toFixed(2)}</span>
                        </span>
                        <Badge bg={statusVariant} className="text-uppercase">{order.status}</Badge>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body className="history-accordion-body border-0 p-3">
                      <Table responsive size="sm" bordered hover className="history-table mb-0 border-0">
                        <thead>
                          <tr className="history-table-header small text-uppercase">
                            <th>Medicine Name</th>
                            <th className="text-center">Ordered Qty</th>
                            <th className="text-center">Unit Price</th>
                            <th className="text-right">Subtotal</th>
                            <th className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.items && order.items.map((item, idx) => (
                            <tr key={idx} className="align-middle">
                              <td>
                                <div className="fw-bold text-info">{item.medicine_name}</div>
                                <div className="text-muted small">Batch: {item.batch_number || 'N/A'} | Category: {item.category || 'N/A'}</div>
                              </td>
                              <td className="text-center fw-bold">
                                {item.quantity} <span className="text-uppercase small text-muted">{(item.selected_unit || 'piece')}</span>
                              </td>
                              <td className="text-center">
                                {currencySymbol}{parseFloat(item.unit_price).toFixed(2)}
                              </td>
                              <td className="text-right fw-semibold">
                                {currencySymbol}{parseFloat(item.subtotal).toFixed(2)}
                              </td>
                              <td className="text-center">
                                <Button 
                                  variant="success" 
                                  size="sm" 
                                  className="d-inline-flex align-items-center gap-1"
                                  onClick={() => handleAddFromHistory(item)}
                                >
                                  <FaShoppingCart size={12} /> Add to Cart
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          )}
        </Modal.Body>
      </Modal>

      {/* Prescription Image Viewer Modal */}
      <Modal 
        show={showPrescriptionViewerModal} 
        onHide={() => setShowPrescriptionViewerModal(false)} 
        centered 
        size="lg"
        dialogClassName="order-modal-dialog-lg"
        backdropClassName="order-modal-backdrop"
      >
        <Modal.Header closeButton className="order-modal-header py-2 px-3">
          <div className="d-flex align-items-center gap-2">
            <FaFilePrescription className="text-warning" size={18} />
            <Modal.Title className="fs-6 mb-0 text-white">Prescription Document Preview</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body className="p-3 text-center bg-dark" style={{ minHeight: '350px' }}>
          {activePrescriptionViewUrl ? (
            <div className="d-flex flex-column align-items-center">
              <div 
                className="overflow-auto w-100 d-flex justify-content-center align-items-center p-2 rounded" 
                style={{ maxHeight: '70vh', background: 'rgba(0,0,0,0.6)' }}
              >
                <img 
                  src={activePrescriptionViewUrl} 
                  alt="Prescription Full View" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '65vh', 
                    objectFit: 'contain',
                    transform: `scale(${rxZoom}) rotate(${rxRotate}deg)`,
                    transition: 'transform 0.2s ease',
                    borderRadius: '8px'
                  }} 
                />
              </div>

              {/* Zoom & Rotation Controls */}
              <div className="mt-3 d-flex align-items-center justify-content-center gap-2">
                <Button 
                  variant="outline-light" 
                  size="sm" 
                  onClick={() => setRxZoom(z => Math.max(0.5, z - 0.25))}
                  title="Zoom Out"
                >
                  <FaSearchMinus /> Zoom Out
                </Button>
                <Button 
                  variant="outline-light" 
                  size="sm" 
                  onClick={() => { setRxZoom(1); setRxRotate(0); }}
                  title="Reset Zoom"
                >
                  Reset ({Math.round(rxZoom * 100)}%)
                </Button>
                <Button 
                  variant="outline-light" 
                  size="sm" 
                  onClick={() => setRxZoom(z => Math.min(3, z + 0.25))}
                  title="Zoom In"
                >
                  <FaSearchPlus /> Zoom In
                </Button>
                <Button 
                  variant="outline-info" 
                  size="sm" 
                  onClick={() => setRxRotate(r => (r + 90) % 360)}
                  title="Rotate Image"
                >
                  <FaRedo /> Rotate
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-muted p-4">No image available for this prescription</div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
}
